/**
 * Submit / approve / reject / reopen for string registry entries (same roles as config registry).
 */

import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import { useGlobalConfig } from "context/GlobalContext";
import { StringRegistryApis } from "../api/stringRegistryAPI";
import { StringRegistryItem } from "../types/stringRegistryTypes";

interface StringRegistryWorkflowButtonsProps {
  entry: StringRegistryItem;
  isDirty?: boolean;
  onSaveFirst?: () => Promise<void>;
  onActionComplete: (updated: StringRegistryItem) => Promise<void>;
  onError: (message: string) => void;
}

type ActionType = "approve" | "reject" | "reopen" | "submit" | null;

export const StringRegistryWorkflowButtons = ({
  entry,
  isDirty = false,
  onSaveFirst,
  onActionComplete,
  onError
}: StringRegistryWorkflowButtonsProps) => {
  const { rc } = useGlobalConfig();
  const [isLoading, setIsLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ActionType>(null);

  const canApprove = rc.permissionsManager.canConfigRegistryApprove();
  const canEdit = rc.permissionsManager.canStringRegistryEdit();
  const verificationStatus = entry.verificationStatus;

  const showSubmit =
    (verificationStatus === "TODO" || verificationStatus === "REJECTED") && canEdit;
  const showApprove = verificationStatus === "READY_FOR_QA" && canApprove;
  const showReject = verificationStatus === "READY_FOR_QA" && canApprove;
  const showReopen = verificationStatus === "APPROVED" && canApprove;

  const handleAction = async (action: ActionType) => {
    if (!action) return;

    if (action === "submit" && isDirty && onSaveFirst) {
      try {
        await onSaveFirst();
      } catch (err: any) {
        onError(err.message || "Failed to save changes before submitting");
        setConfirmAction(null);
        return;
      }
    }

    setIsLoading(true);
    try {
      let response;
      switch (action) {
        case "submit":
          response = await StringRegistryApis.submitStringEntry(rc, entry.documentId);
          break;
        case "approve":
          response = await StringRegistryApis.approveStringEntry(rc, entry.documentId);
          break;
        case "reject":
          response = await StringRegistryApis.rejectStringEntry(rc, entry.documentId);
          break;
        case "reopen":
          response = await StringRegistryApis.reopenStringEntry(rc, entry.documentId);
          break;
      }

      if (response?.error) {
        onError(response.error.message || `Failed to ${action}`);
      } else if (response?.data) {
        const payload = (response.data as any).data ?? response.data;
        await onActionComplete(payload as StringRegistryItem);
      } else {
        onError(`Unexpected response from ${action}`);
      }
    } catch (err: any) {
      onError(err.message || `Failed to ${action}`);
    } finally {
      setIsLoading(false);
      setConfirmAction(null);
    }
  };

  const label = entry.displayName || entry.stringResourceId;

  const getConfirmDialogContent = () => {
    switch (confirmAction) {
      case "submit":
        return {
          title: "Submit for review",
          content: isDirty
            ? `Your changes will be saved and "${label}" will be submitted for review.`
            : `Submit "${label}" for review?`,
          confirmText: isDirty ? "Save & submit" : "Submit",
          confirmColor: "primary" as const
        };
      case "approve":
        return {
          title: "Approve string",
          content: `Approve "${label}"?`,
          confirmText: "Approve",
          confirmColor: "success" as const
        };
      case "reject":
        return {
          title: "Reject string",
          content: `Reject "${label}"? The editor can fix issues and resubmit.`,
          confirmText: "Reject",
          confirmColor: "error" as const
        };
      case "reopen":
        return {
          title: "Reopen for editing",
          content: `Reopen "${label}" for editing? Status will return to TODO.`,
          confirmText: "Reopen",
          confirmColor: "warning" as const
        };
      default:
        return null;
    }
  };

  const dialogContent = getConfirmDialogContent();

  if (!showSubmit && !showApprove && !showReject && !showReopen) {
    return null;
  }

  return (
    <>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
        {showSubmit && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<SendIcon />}
            onClick={() => setConfirmAction("submit")}
            disabled={isLoading}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {isDirty ? "Save & submit for review" : "Submit for review"}
          </Button>
        )}
        {showApprove && (
          <Button
            variant="contained"
            color="success"
            size="small"
            startIcon={<CheckCircleIcon />}
            onClick={() => setConfirmAction("approve")}
            disabled={isLoading}
          >
            Approve
          </Button>
        )}
        {showReject && (
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<CancelIcon />}
            onClick={() => setConfirmAction("reject")}
            disabled={isLoading}
          >
            Reject
          </Button>
        )}
        {showReopen && (
          <Button
            variant="outlined"
            color="warning"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => setConfirmAction("reopen")}
            disabled={isLoading}
          >
            Reopen for editing
          </Button>
        )}
      </Box>

      {dialogContent && (
        <Dialog open={confirmAction !== null} onClose={() => setConfirmAction(null)} maxWidth="sm" fullWidth>
          <DialogTitle>{dialogContent.title}</DialogTitle>
          <DialogContent>
            <DialogContentText>{dialogContent.content}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmAction(null)} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              onClick={() => handleAction(confirmAction)}
              color={dialogContent.confirmColor}
              variant="contained"
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
            >
              {isLoading ? "Processing…" : dialogContent.confirmText}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default StringRegistryWorkflowButtons;
